<template>
  <div class="event-center-page">
    <!-- 页面标题 -->
    <div class="page-title">
      <h1 class="title-text">全部事件{{ totalEventCount }}条</h1>
    </div>

    <!-- 事件列表容器 -->
    <div class="events-container">
      <!-- 紧急告警列 -->
      <div class="event-column emergency-column">
        <div class="column-header">
          <span class="column-title">紧急告警</span>
          <span class="column-count" v-if="alertTotal > 0">{{ alertTotal }}</span>
        </div>
        <div class="event-list" ref="emergencyListRef" :style="{ '--line-height': emergencyLineHeight + 'px' }"
          @scroll="handleScroll($event, 'emergency')">
          <div v-for="event in displayedEmergencyAlarms" :key="event.id" class="event-item">
            <div class="event-dot"></div>
            <div class="event-content">
              <span class="event-time">{{ event.time }}</span>
              <div class="event-text markdown-wrapper">
                <XMarkdown :markdown="event.content" :enable-breaks="true" />
              </div>
              <a href="javascript:;" class="event-action" @click="handleView(event)">
                立即查看
                <el-icon class="action-icon">
                  <ArrowRight />
                </el-icon>
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- 待办事项列 -->
      <div class="event-column todo-column">
        <div class="column-header">
          <span class="column-title">待办事项</span>
          <span class="column-count" v-if="taskTotal > 0">{{ taskTotal }}</span>
        </div>
        <div class="event-list" ref="todoListRef" :style="{ '--line-height': todoLineHeight + 'px' }"
          @scroll="handleScroll($event, 'todo')">
          <div v-for="event in displayedTodoItems" :key="event.id" class="event-item">
            <div class="event-dot"></div>
            <div class="event-content">
              <span class="event-time">{{ event.time }}</span>
              <div class="event-text markdown-wrapper">
                <XMarkdown :markdown="event.content" :enable-breaks="true" />
              </div>
              <a href="javascript:;" class="event-action" @click="handleAction(event)">
                去处理
                <el-icon class="action-icon">
                  <ArrowRight />
                </el-icon>
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- 提醒列 -->
      <div class="event-column reminder-column">
        <div class="column-header">
          <span class="column-title">提醒</span>
          <span class="column-count" v-if="reminderTotal > 0">{{ reminderTotal }}</span>
        </div>
        <div class="event-list" ref="reminderListRef" :style="{ '--line-height': reminderLineHeight + 'px' }"
          @scroll="handleScroll($event, 'reminder')">
          <div v-for="event in displayedReminders" :key="event.id" class="event-item">
            <div class="event-dot"></div>
            <div class="event-content">
              <span class="event-time">{{ event.time }}</span>
              <div class="event-text markdown-wrapper">
                <XMarkdown :markdown="event.content" :enable-breaks="true" />
              </div>
              <a href="javascript:;" class="event-action" @click="handleContact(event)">
                查看状态
                <el-icon class="action-icon">
                  <ArrowRight />
                </el-icon>
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- 通知列 -->
      <div class="event-column notification-column">
        <div class="column-header">
          <span class="column-title">通知</span>
          <span class="column-count" v-if="notificationTotal > 0">{{ notificationTotal }}</span>
        </div>
        <div class="event-list" ref="notificationListRef" :style="{ '--line-height': notificationLineHeight + 'px' }"
          @scroll="handleScroll($event, 'notification')">
          <div v-for="event in displayedNotifications" :key="event.id" class="event-item">
            <div class="event-dot"></div>
            <div class="event-content">
              <span class="event-time">{{ event.time }}</span>
              <div class="event-text markdown-wrapper">
                <XMarkdown :markdown="event.content" :enable-breaks="true" />
              </div>
              <a href="javascript:;" class="event-action" @click="handleView(event)">
                立即查看
                <el-icon class="action-icon">
                  <ArrowRight />
                </el-icon>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch, nextTick, onUnmounted, onActivated, shallowRef } from 'vue'
import { XMarkdown } from 'vue-element-plus-x'
import useMessageStore from '@/store/modules/message'
import useUserStore from '@/store/modules/user'
import { ackMessage, getPushMessages } from '@/api/message'
import { ArrowRight } from '@element-plus/icons-vue'

const messageStore = useMessageStore()
const userStore = useUserStore()

// 列表引用
const emergencyListRef = ref(null)
const todoListRef = ref(null)
const reminderListRef = ref(null)
const notificationListRef = ref(null)

// 线条高度
const emergencyLineHeight = ref(0)
const todoLineHeight = ref(0)
const reminderLineHeight = ref(0)
const notificationLineHeight = ref(0)

// 分页状态
const PAGE_SIZE = 20 // 每页加载20条数据

// 每个类型的分页状态
const paginationState = {
  alert: { page: 1, hasMore: true, total: 0 },
  task: { page: 1, hasMore: true, total: 0 },
  reminder: { page: 1, hasMore: true, total: 0 },
  notification: { page: 1, hasMore: true, total: 0 }
}

// 每个类型的总数（从接口返回的 pagination.total 获取）
const alertTotal = ref(0)
const taskTotal = ref(0)
const reminderTotal = ref(0)
const notificationTotal = ref(0)

// 每个类型的加载状态
const loadingStates = {
  alert: false,
  task: false,
  reminder: false,
  notification: false
}

// 防抖定时器
let resizeTimer = null
let heightCalcTimer = null
const scrollTimers = {
  emergency: null,
  todo: null,
  reminder: null,
  notification: null
}

// 计算线条高度（基于已加载的数据）
function calculateLineHeight(listRef) {
  if (!listRef || !listRef.value) return 0

  const listElement = listRef.value
  const items = listElement.querySelectorAll('.event-item')

  if (items.length === 0) return 0

  let totalHeight = 0
  items.forEach((item, index) => {
    totalHeight += item.offsetHeight
    if (index < items.length - 1) {
      totalHeight += 16 // 间距
    }
  })

  return totalHeight
}

// 更新所有线条高度
function updateAllLineHeights() {
  if (heightCalcTimer) {
    cancelAnimationFrame(heightCalcTimer)
  }

  heightCalcTimer = requestAnimationFrame(() => {
    nextTick(() => {
      emergencyLineHeight.value = calculateLineHeight(emergencyListRef)
      todoLineHeight.value = calculateLineHeight(todoListRef)
      reminderLineHeight.value = calculateLineHeight(reminderListRef)
      notificationLineHeight.value = calculateLineHeight(notificationListRef)
    })
  })
}

// 紧急告警数据（使用 shallowRef 优化性能）
const emergencyAlarms = shallowRef([])

// 待办事项数据
const todoItems = shallowRef([])

// 提醒数据
const reminders = shallowRef([])

// 通知数据
const notifications = shallowRef([])

// 显示所有已加载的数据（分页加载）
const displayedEmergencyAlarms = computed(() => emergencyAlarms.value)
const displayedTodoItems = computed(() => todoItems.value)
const displayedReminders = computed(() => reminders.value)
const displayedNotifications = computed(() => notifications.value)

// 滚动加载更多（触底加载下一页）
function handleScroll(event, type) {
  // 清除该类型的滚动定时器
  if (scrollTimers[type]) {
    clearTimeout(scrollTimers[type])
  }

  // 防抖处理，避免频繁触发
  scrollTimers[type] = setTimeout(() => {
    const target = event.target
    const scrollTop = target.scrollTop
    const scrollHeight = target.scrollHeight
    const clientHeight = target.clientHeight

    // 当滚动到距离底部100px时加载更多
    if (scrollHeight - scrollTop - clientHeight < 100) {
      // 根据类型加载对应的下一页
      const typeMap = {
        emergency: 'alert',
        todo: 'task',
        reminder: 'reminder',
        notification: 'notification'
      }
      const apiType = typeMap[type]
      if (apiType && paginationState[apiType].hasMore && !loadingStates[apiType]) {
        loadMoreMessagesByType(apiType)
      }
    }
  }, 150) // 150ms 防抖
}

// 计算总事件数量（使用各类型的 total）
const totalEventCount = computed(() => {
  return alertTotal.value + taskTotal.value + reminderTotal.value + notificationTotal.value
})

// 格式化时间
function formatTime(dateString) {
  if (!dateString) return ''
  try {
    const date = new Date(dateString)
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    const seconds = String(date.getSeconds()).padStart(2, '0')
    return `${year}/${month}/${day}  ${hours}:${minutes}:${seconds}`
  } catch (e) {
    return dateString
  }
}

// 转换消息数据为事件中心格式（优化：分批处理大量数据）
function convertMessagesToEvents(messages) {
  const alerts = []
  const tasks = []
  const reminderList = []
  const notificationList = []

  if (!messages || !Array.isArray(messages)) {
    return { alerts, tasks, reminderList, notificationList }
  }

  // 使用 for 循环代替 forEach，性能更好
  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i]
    const type = msg.type || msg.content?.type || ''
    const content = msg.content?.content || {}
    const body = content.body || ''
    const title = content.title || ''
    const time = formatTime(msg.createdAt || msg.sentAt || msg.content?.timestamp)

    const eventItem = {
      id: msg.id || `event-${i}-${Date.now()}`, // 添加唯一ID用于key
      content: body || title || '无内容',
      time: time,
      originalMessage: msg // 保留原始消息数据，方便后续使用
    }

    if (type === 'alert') {
      alerts.push(eventItem)
    } else if (type === 'task') {
      tasks.push(eventItem)
    } else if (type === 'reminder') {
      reminderList.push(eventItem)
    } else if (type === 'notification') {
      notificationList.push(eventItem)
    }
  }

  return { alerts, tasks, reminderList, notificationList }
}


// 加载指定类型的消息数据（分页）
async function loadMessagesByType(type, page = 1, append = false) {
  // 如果该类型正在加载
  if (loadingStates[type]) return

  loadingStates[type] = true

  try {
    const userId = userStore.userId || ''
    if (!userId) {
      loadingStates[type] = false
      return
    }

    const response = await getPushMessages({
      userId: userId,
      types: type,
      page: page,
      pageSize: PAGE_SIZE
    })

    if (response && response.data) {
      const messages = response.data.messages || []
      const pagination = response.data.pagination || {}

      // 处理消息数据
      if (messages.length > 0) {
        processMessagesDataByType(type, messages, append)
      }

      // 更新分页状态和总数
      const state = paginationState[type]
      state.page = pagination.page || page
      state.hasMore = pagination.hasNext || false
      state.total = pagination.total || 0

      // 更新对应类型的总数
      if (type === 'alert') {
        alertTotal.value = state.total
      } else if (type === 'task') {
        taskTotal.value = state.total
      } else if (type === 'reminder') {
        reminderTotal.value = state.total
      } else if (type === 'notification') {
        notificationTotal.value = state.total
      }

      // 更新线条高度
      nextTick(() => {
        updateAllLineHeights()
      })
    }
  } catch (error) {
    console.error(`加载${type}消息失败:`, error)
  } finally {
    loadingStates[type] = false
  }
}

// 处理指定类型的消息数据
function processMessagesDataByType(type, messages, append = false) {
  if (!messages || messages.length === 0) return false

  const { alerts, tasks, reminderList, notificationList } = convertMessagesToEvents(messages)

  if (append) {
    // 追加数据
    if (type === 'alert') {
      emergencyAlarms.value = [...emergencyAlarms.value, ...alerts]
    } else if (type === 'task') {
      todoItems.value = [...todoItems.value, ...tasks]
    } else if (type === 'reminder') {
      reminders.value = [...reminders.value, ...reminderList]
    } else if (type === 'notification') {
      notifications.value = [...notifications.value, ...notificationList]
    }
  } else {
    // 替换数据
    if (type === 'alert') {
      emergencyAlarms.value = alerts
    } else if (type === 'task') {
      todoItems.value = tasks
    } else if (type === 'reminder') {
      reminders.value = reminderList
    } else if (type === 'notification') {
      notifications.value = notificationList
    }
  }

  return true
}

// 加载所有类型的消息（初始化时调用）
async function loadAllMessages() {
  // 并行加载所有类型的第一页数据
  await Promise.all([
    loadMessagesByType('alert', 1, false),
    loadMessagesByType('task', 1, false),
    loadMessagesByType('reminder', 1, false),
    loadMessagesByType('notification', 1, false)
  ])
}

// 加载更多消息（指定类型的下一页）
async function loadMoreMessagesByType(type) {
  const state = paginationState[type]
  if (!state.hasMore || loadingStates[type]) return
  await loadMessagesByType(type, state.page + 1, true)
}

// 页面挂载时加载消息数据
onMounted(async () => {
  // 加载所有类型的第一页数据
  await loadAllMessages()

  // 监听窗口大小变化，重新计算线条高度（防抖）
  const debouncedResize = () => {
    if (resizeTimer) {
      clearTimeout(resizeTimer)
    }
    resizeTimer = setTimeout(() => {
      updateAllLineHeights()
    }, 300)
  }
  window.addEventListener('resize', debouncedResize)
})

// 页面激活时（从其他页面返回时）快速更新
onActivated(() => {
  if (emergencyAlarms.value.length > 0 || todoItems.value.length > 0 ||
    reminders.value.length > 0 || notifications.value.length > 0) {
    nextTick(() => {
      updateAllLineHeights()
    })
  } else {
    loadAllMessages()
  }
})

// 组件卸载时移除事件监听
onUnmounted(() => {
  window.removeEventListener('resize', updateAllLineHeights)
  if (resizeTimer) {
    clearTimeout(resizeTimer)
  }
  if (heightCalcTimer) {
    clearTimeout(heightCalcTimer)
  }
  // 清除所有滚动定时器
  Object.values(scrollTimers).forEach(timer => {
    if (timer) {
      clearTimeout(timer)
    }
  })
})

// 监听数据变化，重新计算线条高度
watch([emergencyAlarms, todoItems, reminders, notifications], () => {
  if (heightCalcTimer) {
    cancelAnimationFrame(heightCalcTimer)
  }
  heightCalcTimer = requestAnimationFrame(() => {
    updateAllLineHeights()
  })
}, { flush: 'post' })

// 确认消息已读
async function confirmMessageRead(event) {
  const messageId = event.originalMessage.id

  const userId = userStore.userId || ''
  await ackMessage({
    messageId,
    action: 'read',
    userId
  })
}

function handleView(event) {
  confirmMessageRead(event)
  // 处理查看逻辑
}

function handleAction(event) {
  confirmMessageRead(event)
  // 处理操作逻辑
}

function handleContact(event) {
  confirmMessageRead(event)
  // 处理查看状态逻辑
}
</script>

<style scoped lang="scss">
.event-center-page {
  padding: 0px 20px 0 0;
  min-height: 100vh;
  background: transparent;
}

.page-title {
  margin-bottom: 24px;
}

.title-text {
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-weight: 500;
  font-size: 22px;
  line-height: normal;
  color: #2e2e2e;
  margin: 0;
}

.events-container {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  max-width: 1700px;
}

.event-column {
  // 卡片背景色：白色
  background: #ffffff;
  border-radius: 12px;
  padding: 24px 20px;
  height: calc(100vh - 200px);
  max-height: calc(100vh - 200px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  min-height: 0;
}

// 紧急告警列特殊样式：橙色渐变效果
.emergency-column {
  background: linear-gradient(172deg,
      rgba(255, 236, 231, 0.90) -5.12%,
      rgba(255, 255, 255, 0.60) 27.04%);
  border: 1px solid #FFF;
  backdrop-filter: blur(2px);
}

// 待办事项列特殊样式：蓝色渐变效果
.todo-column {
  background: linear-gradient(172deg,
      rgba(231, 237, 255, 0.90) -5.12%,
      rgba(255, 255, 255, 0.60) 27.04%);
  border: 1px solid #FFF;
  backdrop-filter: blur(2px);
}

// 提醒列特殊样式：绿色渐变效果
.reminder-column {
  background: linear-gradient(172deg,
      rgba(231, 255, 245, 0.90) -5.12%,
      rgba(255, 255, 255, 0.60) 27.04%);
  border: 1px solid #FFF;
  backdrop-filter: blur(2px);
}

// 通知列特殊样式：紫色渐变效果
.notification-column {
  background: linear-gradient(172deg,
      rgba(237, 231, 255, 0.90) -5.12%,
      rgba(255, 255, 255, 0.60) 27.04%);
  border: 1px solid #FFF;
  backdrop-filter: blur(2px);
}

.column-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.column-title {
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-weight: 400;
  font-size: 15px;
  line-height: 18px;
  color: #2e2e2e;
}

.column-count {
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-weight: 400;
  font-size: 15px;
  line-height: 18px;
  color: #2e2e2e;
}

.event-list {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: relative;
  padding-left: 12px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 0;
  min-height: 0;
  max-height: 100%;

  // 自定义滚动条样式
  &::-webkit-scrollbar {
    display: none;
  }
}

.event-list::before {
  content: '';
  position: absolute;
  left: 3px;
  top: 0;
  // 使用 CSS 变量动态设置高度
  height: var(--line-height, 0);
  width: 2px;
  background: #e0e0e0;
}

.event-item {
  display: flex;
  gap: 12px;
  position: relative;
  padding-bottom: 16px;
  flex-shrink: 0;
}

// 最后一个项目不需要底部间距
.event-item:last-child {
  padding-bottom: 0;
}

.event-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #e0e0e0;
  flex-shrink: 0;
  margin-top: 6px;
  position: relative;
  z-index: 1;
  margin-left: -11px;
}

.event-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.event-time {
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-weight: 400;
  font-size: 11px;
  line-height: 13px;
  color: rgba(46, 46, 46, 0.7);
  white-space: nowrap;
  order: 1; // 时间在最上面
}

.event-text {
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-weight: 400;
  font-size: 13px;
  line-height: 19px;
  color: #2e2e2e;
  margin: 0;
  word-break: break-word;
  order: 2; // 文字在中间
}

.markdown-wrapper {
  padding: 0;
  margin: 0;
  font-family: 'HarmonyOS Sans SC', sans-serif;
  line-height: 1.45;

  :deep(.elx-xmarkdown-container) {
    background: transparent;
    padding: 0;
  }

  :deep(p) {
    margin: 2px 0;
    font-size: 13px;
    line-height: 20px;
    color: #2e2e2e;
  }

  :deep(ul),
  :deep(ol) {
    margin: 6px 0 6px 16px;
    padding: 0;
    list-style-position: outside;
  }

  :deep(li) {
    line-height: 20px;
    color: #2e2e2e;
    padding-left: 2px;
  }

  :deep(li + li) {
    margin-top: 4px;
  }

  :deep(a) {
    color: #3a68ff;
    text-decoration: none;
  }

  :deep(a:hover) {
    text-decoration: underline;
  }
}

.event-action {
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-weight: 400;
  font-size: 11px;
  line-height: 12px;
  color: #3a68ff;
  text-decoration: none;
  cursor: pointer;
  transition: opacity 0.3s;
  display: flex;
  align-items: center;
  order: 3; // 操作链接在最下面
}

.event-action:hover {
  opacity: 0.8;
}

.action-icon {
  width: 10px;
  height: 10px;
  flex-shrink: 0;
}
</style>
